import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import type { TicketMessageMentionDto } from "@/api/tickets";
import { useTicketApi } from "@/api/tickets";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/locales/i18n-provider";
import { identityLabelOf, initialsOf } from "@/utils/person-name";

const PROFILE_STALE_TIME_MS = 5 * 60 * 1000;
const HOVER_OPEN_DELAY_MS = 1000;

export function AgentMentionHoverCard({ children, mention, ticketId }: { children: ReactNode; mention: TicketMessageMentionDto; ticketId: string }) {
  const { t } = useI18n();
  const ticketApi = useTicketApi();
  const [open, setOpen] = useState(false);

  const profileQuery = useQuery({
    queryKey: ["tickets", "mention-agent", ticketId, mention.agentId],
    queryFn: async ({ signal }) => {
      const groups = await ticketApi.getMentionCandidates(ticketId, { filter: mention.displayName, maxPerGroup: 20 }, signal);
      return groups.flatMap((group) => group.agents).find((candidate) => candidate.agentId === mention.agentId) ?? null;
    },
    enabled: open,
    staleTime: PROFILE_STALE_TIME_MS,
  });
  const profile = profileQuery.data;

  return (
    <HoverCard closeDelay={200} open={open} openDelay={HOVER_OPEN_DELAY_MS} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>
        <button
          className="rounded-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          type="button"
        >
          {children}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-72" sideOffset={6}>
        <div className="flex items-start gap-3">
          <Avatar className="size-8 shrink-0">
            {profile?.avatarUrl ? <AvatarImage alt={mention.displayName} src={profile.avatarUrl} /> : null}
            <AvatarFallback>{initialsOf(mention.displayName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-foreground">{mention.displayName}</p>
            <p className="text-xs text-muted-foreground">{t("user.supportAgent")}</p>
            {profileQuery.isPending ? (
              <Skeleton className="mt-2 h-3.5 w-32" />
            ) : profile && identityLabelOf(profile) ? (
              <p className="mt-2 truncate text-xs text-muted-foreground">{identityLabelOf(profile)}</p>
            ) : null}
          </div>
        </div>
        <div className="mt-2.5 pl-10">
          {profile?.isAssignee || profile?.isParticipant ? (
            <div className="flex flex-wrap gap-1.5">
              {profile.isAssignee ? <Badge variant="secondary">{t("tickets.fields.assignee")}</Badge> : null}
              {profile.isParticipant ? <Badge variant="outline">{t("tickets.fields.participants")}</Badge> : null}
            </div>
          ) : null}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
